import {
  MoveTextContentToCacheAction,
  RedoAction,
  SaveSnapshotAction,
  TextCacheAddItemAction,
  TextCacheRemoveItemAction,
  UndoAction
} from '@src/actions/wysiwyg/editor';
import {
  WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE,
  WYSIWYG_EDITOR_REDO,
  WYSIWYG_EDITOR_SAVE_SNAPSHOT,
  WYSIWYG_EDITOR_TEXT_CACHE_ADD_ITEM,
  WYSIWYG_EDITOR_TEXT_CACHE_REMOVE_ITEM,
  WYSIWYG_EDITOR_UNDO
} from '@src/constants/wysiwyg_editor';
import type {
  CacheStore,
  TTextBoxContentItem
} from '../../types/wysiwyg_editor';

const initialState: CacheStore = {
  history: {
    undo: [],
    redo: []
  },
  text: []
};

type Action =
  | RedoAction
  | TextCacheAddItemAction
  | TextCacheRemoveItemAction
  | MoveTextContentToCacheAction
  | SaveSnapshotAction
  | UndoAction;

function newHistoryEvent(state: CacheStore) {
  return { text: state.text };
}

function uuid() {
  return `${Date.now()}-${Math.random() * 10000000}`;
}

function getTextContent(pageItem: TTextBoxContentItem) {
  const tempEl = document.createElement('div');
  tempEl.innerHTML = pageItem.content?.ds || '';
  return tempEl.textContent;
}

function cache(state: CacheStore = initialState, action: Action): CacheStore {
  if (action.type === WYSIWYG_EDITOR_UNDO) {
    const undo = [...state.history.undo];
    const redo = [...state.history.redo];

    if (undo.length === 0) return state;

    const nextState = undo.pop();
    const newEvent = newHistoryEvent(state);

    redo.push(newEvent);

    return {
      ...state,
      ...nextState,
      history: { undo, redo }
    };
  } else if (action.type === WYSIWYG_EDITOR_REDO) {
    const undo = [...state.history.undo];
    const redo = [...state.history.redo];

    if (redo.length === 0) return state;

    const nextState = redo.pop();
    const newEvent = newHistoryEvent(state);

    undo.push(newEvent);

    return {
      ...state,
      ...nextState,
      history: { undo, redo }
    };
  } else if (
    action.type === WYSIWYG_EDITOR_TEXT_CACHE_ADD_ITEM ||
    action.type === WYSIWYG_EDITOR_MOVE_TEXT_CONTENT_TO_CACHE
  ) {
    const newItem = action.payload;
    if (!newItem.content) return state;

    const textContent = getTextContent(newItem);
    if (textContent.trim().length === 0) return state;

    return {
      ...state,
      text: [
        ...state.text,
        {
          item: newItem,
          textContent,
          uuid: uuid()
        }
      ]
    };
  } else if (action.type === WYSIWYG_EDITOR_SAVE_SNAPSHOT) {
    const newEvent = newHistoryEvent(state);

    return {
      ...state,
      history: {
        ...state.history,
        undo: [...state.history.undo, newEvent],
        redo: []
      }
    };
  } else if (action.type === WYSIWYG_EDITOR_TEXT_CACHE_REMOVE_ITEM) {
    return {
      ...state,
      text: state.text.filter((item) => item.uuid !== action.payload)
    };
  } else {
    return state;
  }
}

export default cache;
